import { Typography, theme } from 'antd'

import { useKanbanScale } from '@/Ship/Components/Kanban/Kanban'

import { OffersCountBadge } from './OffersCountBadge'

import type { TransportRequestRelationRef } from './TransportRequest.types'
import type { ReactNode } from 'react'

/** Картка канбану: `id` — контракт Ship-стора (= uuid запису), `number` — «Ід». */
export interface TransportRequestKanbanItem {
    id: string
    uuid: string
    number?: number
    title?: string
    client?: TransportRequestRelationRef | null
    typesCars?: TransportRequestRelationRef[] | null
    loadingCountries?: TransportRequestRelationRef[] | null
    unloadingCountries?: TransportRequestRelationRef[] | null
    /** Лише запит */
    addressLoading?: string | null
    addressUnloading?: string | null
    /** Лише заявка — адреси масивом (`SPEC-021` §4.1) */
    loadingAddresses?: string[] | null
    unloadingAddresses?: string[] | null
    /** Лише тендер — крайній термін (`SPEC-023` §4.5) */
    deadline?: string | null
    countOffers?: number | null
}

/** Рядок картки: підпис жирним і значення (у легасі — «-», коли порожньо). */
export interface KanbanCardRow {
    label: string
    value: ReactNode
}

/** Назви вкладених посилань через «, » (типи авто, країни). */
export const titlesOf = (refs: TransportRequestRelationRef[] | null | undefined): string =>
    (refs ?? [])
        .map((ref) => ref.title)
        .filter((title): title is string => typeof title === 'string' && title !== '')
        .join(', ')

/**
 * Вміст картки канбану запиту чи тендера (легасі `RequestKanbanMode.jsx:76-107`):
 * «Ід» і бейдж кількості пропозицій, далі рядки, які дає власник (у тендера
 * адрес немає). Чистий рендер: клік і перечитування дає Ship `Kanban`.
 *
 * Легасі показував «Тип авто» й «Адресу зав.» з полів, яких у запиту немає
 * (`typeCar`, `addressDownload`), — там завжди стояв «-» (ДП-09); тут —
 * справжні `typesCars` і `addressLoading`.
 *
 * Кольори — токени теми, розміри множаться на масштаб канбану.
 */
export const TransportRequestKanbanCard = ({
    item,
    rows,
    hideOffers = false,
}: {
    item: TransportRequestKanbanItem
    rows: KanbanCardRow[]
    hideOffers?: boolean
}) => {
    const { token } = theme.useToken()
    const scale = useKanbanScale()
    const px = (base: number): number => Math.round(base * scale)

    return (
        <div
            style={{
                background: token.colorBgContainer,
                border: `1px solid ${token.colorBorderSecondary}`,
                borderRadius: token.borderRadiusLG,
                padding: `${px(10)}px ${px(12)}px`,
                display: 'flex',
                flexDirection: 'column',
                gap: px(4),
                fontSize: px(13),
                color: token.colorText,
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: px(6) }}>
                <span>
                    <b>Ід:</b> <Typography.Link>[{item.number ?? '—'}]</Typography.Link>
                </span>
                {hideOffers ? null : <OffersCountBadge count={item.countOffers} />}
            </div>
            {rows.map((row) => (
                <div key={row.label} style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                    <b>{row.label}:</b> {row.value}
                </div>
            ))}
        </div>
    )
}
